import React, { useState } from 'react';
import { BACKEND_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { usePlan } from '../context/PlanContext';

import {
  ArrowRight,
  Check,
  CircleNotch,
  Lock,
  Sparkle,
  X
} from '@phosphor-icons/react';
const PaywallModal = ({ isOpen, onClose, opportunityCount = 12, targetPlan = 'pro' }) => {
  const navigate = useNavigate();
  const { userPlan } = usePlan();
  const [loading, setLoading] = useState(false);

  const lockedFeatures = targetPlan === 'partner_pro' 
    ? [
        'All Pro features',
        'View all client scans',
        'Consultant dashboard',
        'Add consultant notes',
        'Update opportunity status',
        'White-label PDF reports',
        'Priority support',
        'Unlimited team users'
      ]
    : [
        `All ${opportunityCount}+ automation opportunities`,
        'Complete ROI calculations',
        'AI readiness score & grade',
        'Implementation roadmap',
        'Priority scoring',
        'Full PDF report',
        'Historical scans',
        'Team access (up to 5 users)'
      ];

  const pricing = targetPlan === 'partner_pro'
    ? { monthly: 149, yearly: 1490, label: 'Partner Pro' }
    : { monthly: 79, yearly: 790, label: 'Pro' };

  const handleDirectUpgrade = async (planType) => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ plan_type: planType })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Upgrade error:', error);
      alert('Failed to start upgrade process. Please try again.');
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    // For now, redirect to pricing page to see options
    navigate('/pricing');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative glass rounded-3xl p-8 md:p-12 max-w-2xl w-full card-glow"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-6 right-6 p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] transition-colors"
            >
              <X size={24} className="text-[#A0AEC0]" />
            </button>

            {/* Content */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#6366F1]/20 to-[#EC4899]/20 flex items-center justify-center mx-auto mb-6">
                <Lock size={40} className="text-[#6366F1]" />
              </div>

              <h2 className="text-4xl font-bold text-white mb-4">
                {targetPlan === 'partner_pro' ? (
                  <>Unlock <span className="gradient-text">Consultant Tools</span></>
                ) : (
                  <>Unlock Your Full <span className="gradient-text">AI Roadmap</span></>
                )}
              </h2>

              {targetPlan === 'pro' ? (
                <>
                  <p className="text-xl text-[#A0AEC0]">
                    You've seen 1 opportunity.
                  </p>
                  <p className="text-lg text-white font-medium mt-2">
                    Unlock all {opportunityCount}+ opportunities, ROI insights, roadmap, readiness score, and your full PDF report.
                  </p>
                </>
              ) : (
                <p className="text-lg text-white font-medium mt-2">
                  Upgrade to Partner Pro for client dashboards, consultant tools, and white-label reports.
                </p>
              )}
            </div>

            {/* Locked Features */}
            <div className="glass rounded-2xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Sparkle size={20} className="text-[#14F195]" />
                What You'll Get:
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {lockedFeatures.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={20} className="text-[#14F195] flex-shrink-0 mt-0.5" />
                    <span className="text-[#A0AEC0]">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing Highlight */}
            <div className="bg-gradient-to-r from-[#6366F1]/10 to-[#EC4899]/10 rounded-2xl p-6 border border-[#6366F1]/30 mb-8">
              <div className="text-center">
                <p className="text-sm text-[#A0AEC0] mb-2">{pricing.label} Plan</p>
                <p className="text-5xl font-bold text-white mb-2">${pricing.monthly}<span className="text-xl text-[#A0AEC0]">/mo</span></p>
                <p className="text-sm text-[#718096]">or ${pricing.yearly}/year (save 2 months)</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleUpgrade}
                disabled={loading}
                className="flex-1 relative overflow-hidden rounded-full group disabled:opacity-50"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#14F195] via-[#6366F1] to-[#EC4899]"></div>
                <span className="relative z-10 flex items-center justify-center gap-2 py-4 text-white font-bold text-lg">
                  {loading ? (
                    <>
                      <CircleNotch size={20} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      View Pricing Options
                      <ArrowRight size={20} />
                    </>
                  )}
                </span>
              </button>

              <button
                onClick={onClose}
                disabled={loading}
                className="flex-1 py-4 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all disabled:opacity-50"
              >
                Maybe Later
              </button>
            </div>

            <p className="text-center text-sm text-[#718096] mt-6">
              30-day money-back guarantee • Cancel anytime
            </p>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PaywallModal;
